import { useState } from 'react';

interface CreateSessionModalProps {
  onClose: () => void;
  onCreate: (name: string, numPlayers: number) => void;
}

export function CreateSessionModal({ onClose, onCreate }: CreateSessionModalProps) {
  const [name, setName] = useState('');
  const [numPlayers, setNumPlayers] = useState<2 | 3 | 4>(2);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('Please enter a session name');
      return;
    }

    setIsSubmitting(true);
    await onCreate(name.trim(), numPlayers);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full border border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">Create New Session</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition text-2xl"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Session Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Session Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., The Dragon's Lair Adventure"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition"
              autoFocus
            />
          </div>

          {/* Number of Players */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              Number of Players (Teams)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {([2, 3, 4] as const).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setNumPlayers(num)}
                  className={`py-4 px-4 rounded-lg font-bold text-lg transition ${
                    numPlayers === num
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-2">
              {numPlayers} players = {numPlayers} teams (
              {['Blue', 'Red', 'Green', 'Yellow'].slice(0, numPlayers).join(', ')})
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-600 bg-opacity-20 border border-blue-500 rounded-lg p-4">
            <p className="text-blue-300 text-sm">
              ℹ️ The session will be created in <strong>DRAFT</strong> status. You can configure character sheets and then activate it when ready.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              {isSubmitting ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
